// Modal "Editar empresa" da tela /companies (super admin).
// Mockup: "Modal Empresa · Editar (dados, plano, faturamento)".
//
// Substitui o formulário que vivia na aba Empresas de Configurações. Três
// blocos: dados da conta, plano (em cards) e faturamento, mais o card de
// próxima fatura e a ação de desativar no rodapé.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import Switch from "@material-ui/core/Switch";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CampaignIcon from "@material-ui/icons/Announcement";
import BlockIcon from "@material-ui/icons/Block";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import { makeStyles, alpha, useTheme } from "@material-ui/core/styles";
import moment from "moment";

import AerosDialog from "../AerosDialog";
import AerosConfirmModal from "../AerosConfirmModal";
import useSubscription from "../../hooks/useSubscription";
import {
  periodicidadeLabel,
  statusLabel,
} from "../SubscriptionCard/subscriptionCardRules";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import useCompanies from "../../hooks/useCompanies";
import usePlans from "../../hooks/usePlans";
import { useDate } from "../../hooks/useDate";
import {
  dataValida,
  initialsOf,
  formatBRL,
  parseAmount,
} from "../../pages/Companies/companyRules";
import { periodoLabel, recorrenciaLabel } from "../../pages/Plans/planRules";
import { precosExibiveis } from "../PlansManager/planPriceRules";

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const border = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  return {
    headerAvatar: {
      width: 40,
      height: 40,
      borderRadius: 11,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 13,
      fontWeight: 700,
      flex: "none",
      backgroundColor: alpha(AEROS.cyan, 0.14),
      color: stateText(theme, AEROS.cyan, "info"),
    },
    sectionLabel: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: theme.palette.aeros.textTertiary,
      marginBottom: 10,
    },
    accountGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(6, minmax(0,1fr))",
      gap: 12,
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "1fr" },
    },
    span3: { gridColumn: "span 3", [theme.breakpoints.down("xs")]: { gridColumn: "auto" } },
    span2: { gridColumn: "span 2", [theme.breakpoints.down("xs")]: { gridColumn: "auto" } },
    span1: { gridColumn: "span 1", [theme.breakpoints.down("xs")]: { gridColumn: "auto" } },
    columns: {
      display: "grid",
      gridTemplateColumns: "minmax(0,1.2fr) minmax(0,1fr)",
      gap: 16,
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
    },
    planGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(3, minmax(0,1fr))",
      gap: 8,
    },
    planCard: {
      position: "relative",
      textAlign: "left",
      border: `1px solid ${border}`,
      backgroundColor: theme.palette.aeros.surface,
      borderRadius: 12,
      padding: 12,
      cursor: "pointer",
      font: "inherit",
      color: "inherit",
      display: "flex",
      flexDirection: "column",
      gap: 4,
      minWidth: 0,
    },
    planCardActive: {
      borderColor: AEROS.cyan,
      backgroundColor: alpha(AEROS.cyan, 0.08),
    },
    planCheck: {
      position: "absolute",
      top: 10,
      right: 10,
      fontSize: 16,
      color: stateText(theme, AEROS.cyan, "info"),
    },
    planName: { fontSize: 13, fontWeight: 700, color: theme.palette.text.primary },
    planPrice: {
      fontSize: 16,
      fontWeight: 700,
      letterSpacing: "-0.02em",
      fontVariantNumeric: "tabular-nums",
      color: theme.palette.text.primary,
    },
    planPriceActive: { color: stateText(theme, AEROS.cyan, "info") },
    planPer: { fontSize: 10.5, color: theme.palette.text.secondary, fontWeight: 500 },
    planLimits: {
      fontSize: 10.5,
      color: theme.palette.text.secondary,
      lineHeight: 1.5,
    },
    billingGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0,1fr))",
      gap: 10,
    },
    // segmentado Sim/Não do mockup
    segmented: {
      display: "flex",
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${
        isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.1)"
      }`,
      borderRadius: 10,
      padding: 3,
    },
    segment: {
      flex: 1,
      textAlign: "center",
      padding: "5px 6px",
      borderRadius: 8,
      fontSize: 12,
      fontWeight: 600,
      minWidth: 0,
      color: theme.palette.text.secondary,
    },
    segmentActive: {
      backgroundColor: alpha(AEROS.defaultPrimary, 0.2),
      color: theme.palette.text.primary,
    },
    invoiceCard: {
      marginTop: 10,
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${border}`,
      borderRadius: 12,
      padding: "12px 14px",
      display: "flex",
      flexDirection: "column",
      gap: 6,
    },
    invoiceRow: { display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" },
    invoiceValue: {
      fontSize: 20,
      fontWeight: 700,
      letterSpacing: "-0.02em",
      fontVariantNumeric: "tabular-nums",
      color: theme.palette.text.primary,
    },
    invoiceMeta: { fontSize: 12, color: theme.palette.text.secondary },
    invoiceTag: {
      marginLeft: "auto",
      fontSize: 11,
      fontWeight: 600,
      borderRadius: 6,
      padding: "2px 8px",
    },
    invoiceFoot: { fontSize: 11, color: theme.palette.aeros.textTertiary },
    subCard: {
      marginBottom: 14,
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${border}`,
      borderRadius: 12,
      padding: "12px 14px",
      display: "flex",
      flexDirection: "column",
      gap: 6,
    },
    subRow: {
      display: "flex",
      alignItems: "baseline",
      justifyContent: "space-between",
      gap: 8,
      flexWrap: "wrap",
      fontSize: 12,
      color: theme.palette.text.secondary,
      "& b": { color: theme.palette.text.primary, fontWeight: 600 },
    },
    managedNote: {
      fontSize: 11.5,
      color: theme.palette.aeros.textTertiary,
      marginTop: 4,
    },
    disableButton: {
      color: stateText(theme, AEROS.error, "error"),
      fontSize: 12,
      fontWeight: 600,
      textTransform: "none",
      "& .MuiSvgIcon-root": { fontSize: 16 },
    },
  };
});

const CompanyEditModal = ({ open, companyId, onClose, onSaved }) => {
  const m = useAerosModalStyles();
  const classes = useStyles();
  const theme = useTheme();
  const { dateToClient } = useDate();
  const { update } = useCompanies();
  const { list: listPlans } = usePlans();

  const [plans, setPlans] = useState([]);
  const [record, setRecord] = useState(null);
  const [counts, setCounts] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const { byCompany, cancelByCompany } = useSubscription();
  const [sub, setSub] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelando, setCancelando] = useState(false);

  const carregar = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const [empresa, planos] = await Promise.all([
        api.get(`/companies/${companyId}`),
        listPlans(),
      ]);
      const data = empresa.data || {};
      const settings = data.settings || [];
      const campanhas = settings.find((s) => s.key === "campaignsEnabled");
      setRecord({
        id: data.id,
        name: data.name || "",
        email: data.email || "",
        phone: data.phone || "",
        document: data.document || "",
        password: "",
        planId: data.planId || "",
        status: data.status !== false,
        dueDate: data.dueDate ? moment(data.dueDate).format("YYYY-MM-DD") : "",
        paymentMethod: data.paymentMethod || "",
        generateInvoice:
          data.generateInvoice !== undefined ? data.generateInvoice : true,
        // O toggle do mockup sobrescreve a permissão do plano; o backend
        // grava isso como Setting da empresa, não como coluna.
        campaignsEnabled: campanhas ? campanhas.value === "true" : undefined,
        createdAt: data.createdAt,
      });
      setPlans(Array.isArray(planos) ? planos : []);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }

    // Contagens e fatura são complementares: se falharem, o modal continua
    // editável — só o cabeçalho e o card ficam sem número.
    api
      .get(`/companies/${companyId}/counts`)
      .then(({ data }) => setCounts(data))
      .catch(() => setCounts(null));
    byCompany(companyId)
      .then((data) => setSub(data?.subscription || null))
      .catch(() => setSub(null));
    api
      .get(`/invoices/company/${companyId}`)
      .then(({ data }) => setInvoice(data))
      .catch(() => setInvoice(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    if (open) {
      setCounts(null);
      setInvoice(null);
      setSub(null);
      setMostrarSenha(false);
      carregar();
    } else {
      setRecord(null);
    }
  }, [open, carregar]);

  // Assinatura vigente manda no plano, no vencimento e na recorrência: o
  // backend recusa a troca (ERR_COMPANY_MANAGED_BY_SUBSCRIPTION).
  const gerenciada = ["ACTIVE", "PAST_DUE", "PENDING"].includes(sub?.status);

  const handleCancelarAssinatura = async () => {
    setCancelando(true);
    try {
      await cancelByCompany(companyId);
      toast.success(i18n.t("aeros.companies.subscription.canceled"));
      setConfirmCancel(false);
      const data = await byCompany(companyId);
      setSub(data?.subscription || null);
      if (onSaved) onSaved();
    } catch (err) {
      toastError(err);
    } finally {
      setCancelando(false);
    }
  };

  const campo = (nome) => (e) => {
    const valor = e.target.value;
    setRecord((prev) => ({ ...prev, [nome]: valor }));
  };

  const planoAtual = useMemo(
    () => plans.find((p) => p.id === record?.planId),
    [plans, record]
  );

  // Os três cards do mockup: mostra o plano da empresa junto dos mais
  // próximos em preço, para a troca ser uma comparação e não uma lista.
  const planosVisiveis = useMemo(() => {
    if (!plans.length) return [];
    const ordenados = [...plans].sort(
      (a, b) => parseAmount(a.amount) - parseAmount(b.amount)
    );
    if (ordenados.length <= 3) return ordenados;
    const i = ordenados.findIndex((p) => p.id === record?.planId);
    if (i < 0) return ordenados.slice(0, 3);
    const inicio = Math.min(Math.max(0, i - 1), ordenados.length - 3);
    return ordenados.slice(inicio, inicio + 3);
  }, [plans, record]);

  const handleSalvar = async () => {
    if (!record) return;
    setSalvando(true);
    try {
      const payload = { ...record };
      if (!payload.dueDate || !moment(payload.dueDate).isValid()) {
        payload.dueDate = null;
      }
      if (!payload.password) delete payload.password;
      await update(payload);
      toast.success(i18n.t("aeros.companies.modal.saved"));
      if (onSaved) await onSaved();
      onClose();
    } catch (err) {
      toastError(err);
    }
    setSalvando(false);
  };

  const handleDesativar = () => {
    setRecord((prev) => ({ ...prev, status: !prev.status }));
  };

  const subtitulo = useMemo(() => {
    if (!record) return "";
    const partes = [`ID #${record.id}`];
    if (record.createdAt) {
      partes.push(
        i18n.t("aeros.companies.modal.clientSince", {
          date: dateToClient(record.createdAt),
        })
      );
    }
    if (counts) {
      partes.push(
        i18n.t(
          counts.connections === 1
            ? "aeros.companies.modal.connectionOne"
            : "aeros.companies.modal.connections",
          { count: counts.connections }
        )
      );
      partes.push(
        i18n.t(
          counts.users === 1
            ? "aeros.companies.modal.userOne"
            : "aeros.companies.modal.users",
          { count: counts.users }
        )
      );
    }
    return partes.join(" · ");
  }, [record, counts, dateToClient]);

  const renderSegmentado = (valor, onChange) => (
    <div className={classes.segmented}>
      <button
        type="button"
        className={`${classes.segment} ${valor ? classes.segmentActive : ""}`}
        onClick={() => onChange(true)}
        aria-pressed={valor}
      >
        {i18n.t("compaies.table.yes")}
      </button>
      <button
        type="button"
        className={`${classes.segment} ${!valor ? classes.segmentActive : ""}`}
        onClick={() => onChange(false)}
        aria-pressed={!valor}
      >
        {i18n.t("compaies.table.no")}
      </button>
    </div>
  );

  const tomFatura = invoice?.proxima?.atrasada ? AEROS.error : AEROS.success;

  return (
    <AerosDialog
      open={open}
      onClose={onClose}
      width={860}
      title={
        record
          ? `${i18n.t("aeros.companies.modal.title")} · ${record.name}`
          : i18n.t("aeros.companies.modal.title")
      }
      subtitle={subtitulo}
      headerIcon={
        record && (
          <div className={classes.headerAvatar}>{initialsOf(record.name)}</div>
        )
      }
      onSubmit={handleSalvar}
      submitLabel={i18n.t("aeros.companies.modal.save")}
      submitDisabled={!record || loading}
      loading={salvando}
      footerLeft={
        record && (
          <Button
            className={classes.disableButton}
            startIcon={<BlockIcon />}
            onClick={handleDesativar}
          >
            {record.status
              ? i18n.t("aeros.companies.modal.disable")
              : i18n.t("aeros.companies.modal.enable")}
          </Button>
        )
      }
    >
      {!record ? (
        <div className={m.hint}>{i18n.t("aeros.companies.loading")}</div>
      ) : (
        <>
          <div>
            <div className={classes.sectionLabel}>
              {i18n.t("aeros.companies.modal.accountData")}
            </div>
            <div className={classes.accountGrid}>
              <div className={classes.span3}>
                <label className={m.fieldLabel}>
                  {i18n.t("compaies.table.name")} *
                </label>
                <TextField
                  fullWidth
                  variant="outlined"
                  className={m.input}
                  value={record.name}
                  onChange={campo("name")}
                />
              </div>
              <div className={classes.span2}>
                <label className={m.fieldLabel}>
                  {i18n.t("compaies.table.document")}
                </label>
                <TextField
                  fullWidth
                  variant="outlined"
                  className={m.input}
                  value={record.document}
                  onChange={campo("document")}
                />
              </div>
              <div className={classes.span1}>
                <label className={m.fieldLabel}>
                  {i18n.t("compaies.table.active")}
                </label>
                <FormControl variant="outlined" fullWidth className={m.input}>
                  <Select
                    value={record.status}
                    onChange={(e) => {
                      const valor = e.target.value;
                      setRecord((prev) => ({ ...prev, status: valor }));
                    }}
                  >
                    <MenuItem value={true}>{i18n.t("compaies.table.yes")}</MenuItem>
                    <MenuItem value={false}>{i18n.t("compaies.table.no")}</MenuItem>
                  </Select>
                </FormControl>
              </div>

              <div className={classes.span3}>
                <label className={m.fieldLabel}>
                  {i18n.t("aeros.companies.modal.adminEmail")} *
                </label>
                <TextField
                  fullWidth
                  variant="outlined"
                  className={m.input}
                  value={record.email}
                  onChange={campo("email")}
                />
              </div>
              <div className={classes.span2}>
                <label className={m.fieldLabel}>
                  {i18n.t("compaies.table.phone")}
                </label>
                <TextField
                  fullWidth
                  variant="outlined"
                  className={m.input}
                  value={record.phone}
                  onChange={campo("phone")}
                />
              </div>
              <div className={classes.span1}>
                <label className={m.fieldLabel}>
                  {i18n.t("aeros.companies.modal.newPassword")}
                </label>
                <TextField
                  fullWidth
                  variant="outlined"
                  className={m.input}
                  type={mostrarSenha ? "text" : "password"}
                  value={record.password}
                  onChange={campo("password")}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setMostrarSenha((v) => !v)}
                          aria-label={i18n.t(
                            mostrarSenha
                              ? "aeros.companies.modal.hidePassword"
                              : "aeros.companies.modal.showPassword"
                          )}
                        >
                          {mostrarSenha ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </div>
            </div>
          </div>

          <div className={classes.columns}>
            <div>
              <div className={classes.sectionLabel}>
                {i18n.t("compaies.table.plan")}
              </div>
              <div className={classes.planGrid}>
                {planosVisiveis.map((plano) => {
                  const ativo = plano.id === record.planId;
                  return (
                    <button
                      type="button"
                      key={plano.id}
                      className={`${classes.planCard} ${
                        ativo ? classes.planCardActive : ""
                      }`}
                      disabled={gerenciada}
                      onClick={() =>
                        setRecord((prev) => ({ ...prev, planId: plano.id }))
                      }
                      aria-pressed={ativo}
                    >
                      {ativo && <CheckCircleIcon className={classes.planCheck} />}
                      <div className={classes.planName}>{plano.name}</div>
                      <div
                        className={`${classes.planPrice} ${
                          ativo ? classes.planPriceActive : ""
                        }`}
                      >
                        {precosExibiveis(plano).map((p, i) => (
                          <span key={p.period}>
                            {i > 0 ? " · " : ""}
                            {p.label}
                            <span className={classes.planPer}>
                              {` ${periodoLabel(p.period)}`}
                            </span>
                          </span>
                        ))}
                      </div>
                      <div className={classes.planLimits}>
                        {i18n.t("aeros.companies.modal.planLimits", {
                          users: plano.users,
                          connections: plano.connections,
                          queues: plano.queues,
                        })}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className={m.optionBox} style={{ marginTop: 10 }}>
                <CampaignIcon />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className={m.optionTitle}>
                    {i18n.t("aeros.companies.modal.campaigns")}
                  </div>
                  <div className={m.optionDesc}>
                    {i18n.t("aeros.companies.modal.campaignsHint")}
                  </div>
                </div>
                <Switch
                  color="primary"
                  checked={
                    record.campaignsEnabled !== undefined
                      ? record.campaignsEnabled
                      : Boolean(planoAtual?.useCampaigns)
                  }
                  onChange={(e) => {
                    const valor = e.target.checked;
                    setRecord((prev) => ({
                      ...prev,
                      campaignsEnabled: valor,
                    }));
                  }}
                  inputProps={{
                    "aria-label": i18n.t("aeros.companies.modal.campaigns"),
                  }}
                />
              </div>
            </div>

            <div>
              <div className={classes.sectionLabel}>
                {i18n.t("aeros.companies.subscription.title")}
              </div>
              <div className={classes.subCard}>
                {sub ? (
                  <>
                    <div className={classes.subRow}>
                      <span>{i18n.t("aeros.companies.subscription.plan")}</span>
                      <b>
                        {plans.find((p) => p.id === sub.planId)?.name || "—"}
                      </b>
                    </div>
                    <div className={classes.subRow}>
                      <span>{i18n.t("aeros.companies.subscription.periodMethod")}</span>
                      <b>{periodicidadeLabel(sub)}</b>
                    </div>
                    <div className={classes.subRow}>
                      <span>{i18n.t("aeros.companies.subscription.cycle")}</span>
                      <b>
                        {dataValida(sub.cycleStart) && dataValida(sub.cycleEnd)
                          ? `${dateToClient(sub.cycleStart)} - ${dateToClient(sub.cycleEnd)}`
                          : "—"}
                      </b>
                    </div>
                    <div className={classes.subRow}>
                      <span>{i18n.t("aeros.companies.subscription.status")}</span>
                      <b>{statusLabel(sub)}</b>
                    </div>
                    {gerenciada && (
                      <Button
                        className={classes.disableButton}
                        style={{ alignSelf: "flex-start", minHeight: 44 }}
                        onClick={() => setConfirmCancel(true)}
                      >
                        {i18n.t("aeros.companies.subscription.cancel")}
                      </Button>
                    )}
                  </>
                ) : (
                  <div className={classes.invoiceFoot}>
                    {i18n.t("aeros.companies.subscription.none")}
                  </div>
                )}
              </div>
              <div className={classes.sectionLabel}>
                {i18n.t("aeros.companies.modal.billing")}
              </div>
              <div className={classes.billingGrid}>
                <div>
                  <label className={m.fieldLabel}>
                    {i18n.t("compaies.table.dueDate")}
                  </label>
                  <TextField
                    fullWidth
                    type="date"
                    variant="outlined"
                    className={m.input}
                    value={record.dueDate}
                    onChange={campo("dueDate")}
                    disabled={gerenciada}
                    InputLabelProps={{ shrink: true }}
                  />
                </div>
                <div>
                  <label className={m.fieldLabel}>
                    {i18n.t("compaies.table.recurrence")}
                  </label>
                  {/* Somente leitura: com assinatura, é o período escolhido no
                      checkout; sem, a recorrência legada do plano. */}
                  <TextField
                    fullWidth
                    variant="outlined"
                    className={m.input}
                    value={
                      gerenciada
                        ? periodicidadeLabel(sub)
                        : planoAtual
                        ? recorrenciaLabel(planoAtual.recurrence)
                        : "—"
                    }
                    InputProps={{ readOnly: true }}
                    inputProps={{
                      "aria-label": i18n.t("compaies.table.recurrence"),
                      "aria-readonly": true,
                    }}
                    helperText={
                      gerenciada ? undefined : i18n.t("aeros.companies.recurrenceFromPlan")
                    }
                  />
                </div>
                {gerenciada && (
                  <div
                    className={classes.managedNote}
                    style={{ gridColumn: "1 / -1" }}
                    role="note"
                  >
                    {i18n.t("aeros.companies.managedBySubscription")}
                  </div>
                )}
                <div>
                  <label className={m.fieldLabel}>
                    {i18n.t("aeros.companies.modal.paymentMethod")}
                  </label>
                  <TextField
                    fullWidth
                    variant="outlined"
                    className={m.input}
                    value={record.paymentMethod}
                    onChange={campo("paymentMethod")}
                  />
                </div>
                <div>
                  <label className={m.fieldLabel}>
                    {i18n.t("compaies.table.generateInvoice")}
                  </label>
                  {renderSegmentado(record.generateInvoice, (v) =>
                    setRecord((prev) => ({ ...prev, generateInvoice: v }))
                  )}
                </div>
              </div>

              <div className={classes.invoiceCard}>
                <div className={classes.sectionLabel} style={{ marginBottom: 0 }}>
                  {i18n.t("aeros.companies.modal.nextInvoice")}
                </div>
                {invoice?.proxima ? (
                  <>
                    <div className={classes.invoiceRow}>
                      <span className={classes.invoiceValue}>
                        {formatBRL(invoice.proxima.value)}
                      </span>
                      {dataValida(invoice.proxima.dueDate) && (
                        <span className={classes.invoiceMeta}>
                          {i18n.t("aeros.companies.modal.dueOn", {
                            date: dateToClient(invoice.proxima.dueDate),
                          })}
                        </span>
                      )}
                      <span
                        className={classes.invoiceTag}
                        style={{
                          color: stateText(
                            theme,
                            tomFatura,
                            invoice.proxima.atrasada ? "error" : "success"
                          ),
                          backgroundColor: alpha(tomFatura, 0.12),
                        }}
                      >
                        {i18n.t(
                          invoice.proxima.atrasada
                            ? "aeros.companies.modal.late"
                            : "aeros.companies.modal.onTime"
                        )}
                      </span>
                    </div>
                    <div className={classes.invoiceFoot}>
                      {invoice.ultimaPaga && dataValida(invoice.ultimaPaga.dueDate)
                        ? i18n.t("aeros.companies.modal.lastPaid", {
                            date: dateToClient(invoice.ultimaPaga.dueDate),
                          })
                        : i18n.t("aeros.companies.modal.noPaid")}
                    </div>
                  </>
                ) : (
                  <div className={classes.invoiceFoot}>
                    {invoice
                      ? i18n.t("aeros.companies.modal.noOpenInvoice")
                      : i18n.t("aeros.companies.loading")}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
      <AerosConfirmModal
        open={confirmCancel}
        onClose={() => setConfirmCancel(false)}
        onConfirm={handleCancelarAssinatura}
        loading={cancelando}
        tone="warning"
        icon={<BlockIcon />}
        title={i18n.t("aeros.companies.subscription.cancelTitle")}
        description={i18n.t("aeros.companies.subscription.cancelText")}
        confirmLabel={i18n.t("aeros.companies.subscription.cancelConfirm")}
      />
    </AerosDialog>
  );
};

export default CompanyEditModal;
